import React, { useState } from 'react';
import ConnectTagManager from '../tags/connect_tag_manager';
import { Accordion, Button } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFiles, faNote, faTag } from '@fortawesome/pro-solid-svg-icons';
import PropertyNoteForm from './property_note_form';
import { isEmpty } from 'lodash';
import NoteSection from './note_section';
import type { Note } from 'bundles/connect/types';
import TagHeader from '../tags/tag_header';

type Props = {
  hideNote?: boolean,
  initialNotes?: Note[],
  insightsPropertyId: string,
  tags: string[]
}

export default function PropertyOverviewActions({ hideNote, insightsPropertyId, initialNotes, tags }: Props) {
  const [notes, setNotes] = useState<Note[]>(initialNotes || []);
  const [addingNote, setAddingNote] = useState(false);
  const [noteAccordionKey, setNoteAccordionKey] = useState<string | null>(null);
  const [showTagsHeader, setShowTagsHeader] = useState(false);

  const handleNewNote = (e: React.MouseEvent) => {
    setAddingNote(true);
    setNoteAccordionKey('0');
    e.stopPropagation();
  };

  const handleAddNote = (note: Note) => {
    setNotes([note, ...notes]);
    setAddingNote(false);
  };

  const isNote = (note: Note | null) => {
    return note !== null;
  };

  const handleUpdateNote = (noteUpdate: Note) => {
    setNotes(
      notes.map((note) => {
        if (noteUpdate.id === note.id) {
          return isEmpty(noteUpdate.body) ? null : noteUpdate;
        }
        return note;
      }).filter(isNote)
    );
  };

  const handleRemoveNote = (noteId: number) => {
    setNotes(notes.filter(n => n.id != noteId));
  };

  const toggleAccordion = () => {
    if (noteAccordionKey == '0') {
      setNoteAccordionKey(null);
      setAddingNote(false);
    } else {
      setNoteAccordionKey('0');
    }
  };

  const handleKeyboardAccordion = (e: React.KeyboardEvent) => {
    if (['Tab', 'Escape'].includes(e.key)) {
      return;
    }
    e.preventDefault();

    if (['Enter', ' '].includes(e.key)) {
      toggleAccordion();
    }
  };

  const showNotesAccordion = (notes.length > 0) || addingNote;

  return (
    <div
      className="d-flex flex-column gap-3"
      onClick={e => e.stopPropagation()}
    >
      <div>
        {showTagsHeader && (
          <div className="mb-2">
            <TagHeader />
          </div>
        )}
        <div className="d-flex align-items-center gap-2">
          <ConnectTagManager
            buttonProperties={{
              iconLeft: <FontAwesomeIcon icon={faTag} />,
            }}
            initialTags={tags}
            insightsPropertyId={insightsPropertyId}
            setShowTagsHeader={setShowTagsHeader}
          />
          {!hideNote && isEmpty(notes) && (
            <Button
              variant="outline-primary"
              onClick={handleNewNote}
            >
              <FontAwesomeIcon icon={faNote} />
              Add Note
            </Button>
          )}
        </div>
      </div>
      {!hideNote && showNotesAccordion && (
        <Accordion activeKey={noteAccordionKey}>
          <Accordion.Item eventKey="0">
            <h2 className="accordion-header" onClick={toggleAccordion}>
              <div
                className={`d-flex accordion-button bg-white ${noteAccordionKey === null ? 'collapsed' : ''}`}
                role="button"
                tabIndex={0}
                onKeyDown={handleKeyboardAccordion}
              >
                <div className="d-flex align-items-center justify-content-between w-100">
                  <div className="fw-bold" id="notesAccordion">
                    <FontAwesomeIcon className="me-3 text-secondary" icon={faFiles} />
                    Notes {notes.length > 0 && `(${notes.length})`}
                  </div>
                  {!isEmpty(notes) && (
                    <Button
                      variant="text-primary"
                      onClick={handleNewNote}
                    >
                      <FontAwesomeIcon icon={faNote} />
                      Add Note
                    </Button>)}
                </div>
              </div>
            </h2>
            <Accordion.Body className="p-0">
              {addingNote && (
                <PropertyNoteForm
                  insightsPropertyId={insightsPropertyId}
                  onClose={() => setAddingNote(false)}
                  onSave={handleAddNote}
                />
              )}
              {!isEmpty(notes) && notes.map(note => (
                <NoteSection
                  key={note.id}
                  note={note}
                  onRemove={handleRemoveNote}
                  onUpdate={handleUpdateNote}
                />
              ))}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      )}
    </div>
  );
}
